// =============================================================================
// motion.js - the one motion system (UI Excellence item 1).
//
// Every animated surface reads durations and easings from here. Reduced motion
// is honored in ONE place: MOTION.reduced flips true and every helper returns
// zero-duration values, so components never need their own media query.
//
// Rules: animate opacity and transform only. Never width/height/layout, except
// collapseOut, which measures once and animates max-height.
// =============================================================================
const mq = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

export const MOTION = {
  get reduced() { return !!(mq && mq.matches); },
  dur: { instant: 80, fast: 150, base: 220, slow: 320, moment: 400 },
  ease: {
    standard: "cubic-bezier(0.2, 0.8, 0.2, 1)",
    spring:   "cubic-bezier(0.34, 1.56, 0.64, 1)",
    exit:     "cubic-bezier(0.4, 0, 1, 1)",
    linear:   "linear"
  }
};

const ms = (n) => (MOTION.reduced ? 0 : n) + "ms";

// CSS transition string for the usual property set.
export const transition = (props = ["opacity", "transform"], dur = MOTION.dur.base, ease = MOTION.ease.standard) =>
  props.map((p) => p + " " + ms(dur) + " " + ease).join(", ");

// Entrance keyframe by name (defined in styles.css): fadeUp, fadeIn, scaleIn, slideInRight, slideInUp.
export const enter = (name = "fadeUp", dur = MOTION.dur.base, delay = 0, ease = MOTION.ease.standard) =>
  MOTION.reduced ? {} : { animation: "pos-" + name + " " + dur + "ms " + ease + " " + delay + "ms both" };

// Staggered entrance for lists: index-based delay, capped so long lists never feel slow.
export const stagger = (i, step = 28, cap = 12, name = "fadeUp") => enter(name, MOTION.dur.base, Math.min(i, cap) * step);

// Exit: apply, wait dur, then remove the node.
export const exit = (name = "fadeOut", dur = MOTION.dur.fast) =>
  MOTION.reduced ? {} : { animation: "pos-" + name + " " + dur + "ms " + MOTION.ease.exit + " both" };

// The celebration moment: brief scale + brand glow. 400ms, purely visual, then settles.
export const moment = () => (MOTION.reduced ? {} : { animation: "pos-moment " + MOTION.dur.moment + "ms " + MOTION.ease.spring + " both" });

// Height collapse for removed rows. Measure once, then animate max-height + opacity.
export const collapseOut = (el, dur = MOTION.dur.fast) => new Promise((resolve) => {
  if (!el || MOTION.reduced) { resolve(); return; }
  const h = el.getBoundingClientRect().height;
  el.style.overflow = "hidden"; el.style.maxHeight = h + "px"; el.style.opacity = "1";
  el.style.transition = "max-height " + dur + "ms " + MOTION.ease.exit + ", opacity " + dur + "ms " + MOTION.ease.exit + ", margin " + dur + "ms " + MOTION.ease.exit + ", padding " + dur + "ms " + MOTION.ease.exit;
  requestAnimationFrame(() => { el.style.maxHeight = "0px"; el.style.opacity = "0"; el.style.marginTop = "0"; el.style.marginBottom = "0"; el.style.paddingTop = "0"; el.style.paddingBottom = "0"; });
  setTimeout(resolve, dur + 20);
});
